import { useEffect, useState } from 'react';
import getPageHelp, { PageHelpData } from '../web_api/methods/helpService/getPageHelp';

export interface UsePageHelpResult {
  data: PageHelpData | null;
  loading: boolean;
  error: boolean;
  retry: () => void;
}

/**
 * Fetches contextual help content for the active page and exposes request state.
 *
 * @param pageKey Route-derived help key for the current page.
 * @returns Help data plus loading, error, and retry controls for the caller.
 */
function usePageHelp(pageKey: string): UsePageHelpResult {
  const [data, setData] = useState<PageHelpData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let isCancelled = false;

    const loadPageHelp = async () => {
      if (!pageKey) {
        setData(null);
        setLoading(false);
        setError(false);
        return;
      }

      try {
        setLoading(true);
        setError(false);
        setData(null);

        const response = await getPageHelp(pageKey);

        if (!isCancelled) {
          setData(response);
        }
      } catch (loadError) {
        console.error(loadError);

        if (!isCancelled) {
          setData(null);
          setError(true);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadPageHelp();

    return () => {
      isCancelled = true;
    };
  }, [pageKey, retryKey]);

  return {
    data,
    loading,
    error,
    retry: () => setRetryKey((currentRetryKey) => currentRetryKey + 1),
  };
}

export default usePageHelp;
